import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SelectAutocompleteMultiple, SelectAutocompletePagination } from 'components';
import { renderClientName, renderStaffName } from 'utils';
import { formService } from 'store/form/form.service';

/**
 * Note kinds the bulk PDF export covers. `id` is the Form Builder model the api
 * filters templates by; `filters` lists the record pickers that narrow it. A
 * note only knows the record it hangs off, so the api reaches a person through
 * that record — clinical and office notes through the appointment, claim,
 * invoice and goal notes through their client, timesheet notes through staff.
 */
export const noteFormModels = [
    { id: 'CLINICAL_NOTES', name: 'Clinical Notes', filters: ['client', 'provider'] },
    { id: 'CLAIM_NOTES', name: 'Claim Notes', filters: ['client'] },
    { id: 'CLIENT_NOTES', name: 'Client Notes', filters: ['client'] },
    { id: 'GOAL_NOTES', name: 'Goal Notes', filters: ['client'] },
    { id: 'INVOICE_NOTES', name: 'Invoice Notes', filters: ['client'] },
    { id: 'OFFICE_NOTES', name: 'Office Notes', filters: ['client', 'provider'] },
    { id: 'PAYER_NOTES', name: 'Payer Notes', filters: ['payer'] },
    { id: 'PROGRESS_REPORT', name: 'Progress Report Notes', filters: ['client'] },
    { id: 'STAFF_NOTES', name: 'Staff Notes', filters: ['staff'] },
    { id: 'TIMESHEET_NOTE', name: 'Timesheet Notes', filters: ['staff'] },
];

const FORMS_LIMIT = 200;

/**
 * Templates of the selected note kind. Reloads whenever the kind changes and
 * drops any previous pick, since form ids never carry over between kinds.
 */
export const NoteFormsSelect = ({ control, watch, setValue }) => {
    const formModel = watch('formModel');
    const [forms, setForms] = useState([]);
    const [load, setLoad] = useState(false);

    useEffect(() => {
        // Every filter below the kind belongs to the kind — form ids and the
        // record pick would silently narrow the next export if left behind.
        setValue('formIds', []);
        setValue('client', '');
        setValue('staff', '');
        setValue('resourceId', '');
        if (!formModel) {
            setForms([]);
            return;
        }
        let active = true;
        setLoad(true);
        formService
            .getFormsService({ model: formModel, status: 'PUBLISHED', sortType: 'title', orderType: 'ASC', skip: 0, limit: FORMS_LIMIT })
            .then((res) => {
                if (active) setForms(res?.data?.data || []);
            })
            .catch(() => {
                if (active) setForms([]);
            })
            .finally(() => {
                if (active) setLoad(false);
            });
        return () => {
            active = false;
        };
    }, [formModel]);

    return (
        <SelectAutocompleteMultiple
            control={control}
            name={'formIds'}
            label={'Templates'}
            options={forms}
            loading={load}
            multiple={true}
            disabled={!formModel}
            renderValue={(option) => option?.title}
        />
    );
};

/** The record filter that fits the selected note kind. */
export const NoteResourceFilter = ({ control, watch, setValue }) => {
    const formModel = watch('formModel');
    const { fundingSourceList } = useSelector((state) => ({
        fundingSourceList: state.fundingSource.fundingSourceList,
    }));
    const filters = noteFormModels.find((item) => item.id === formModel)?.filters ?? [];

    if (!filters.length) return null;

    return (
        <>
            {filters.includes('client') && (
                <SelectAutocompletePagination
                    uiType={'fromForm'}
                    title={'Client'}
                    name={'client'}
                    handleSelect={(e) => setValue('client', e?.target?.value)}
                    defaultValue={watch('client')}
                    renderValue={(i) => renderClientName(i)}
                    type={'client'}
                />
            )}
            {(filters.includes('staff') || filters.includes('provider')) && (
                <SelectAutocompletePagination
                    uiType={'fromForm'}
                    title={filters.includes('provider') ? 'Provider' : 'Staff'}
                    name={'staff'}
                    handleSelect={(e) => setValue('staff', e?.target?.value)}
                    defaultValue={watch('staff')}
                    renderValue={(i) => renderStaffName(i)}
                    type={'staff'}
                />
            )}
            {filters.includes('payer') && (
                <SelectAutocompleteMultiple
                    control={control}
                    name={'resourceId'}
                    label={'Payer'}
                    options={fundingSourceList?.funders || []}
                    renderValue={(option) => option?.name}
                />
            )}
        </>
    );
};
